import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * The signed-in customer's OWN account-info change requests
 * (orders.account_change_requests). RLS scopes the rows to the caller's active
 * customer, so an unfiltered select returns exactly their requests.
 *
 * Used on Settings → Profile to surface pending / applied / rejected requests
 * (with the reviewer's note) so the customer can see where a proposed change
 * stands. Writes go through customer-request-account-change (service-role).
 */

export type AccountChangeKind = 'address' | 'contact';
export type AccountChangeStatus = 'pending' | 'applied' | 'rejected';

export interface AccountChangeRequest {
  id: string;
  locationId: string | null;
  kind: AccountChangeKind;
  currentValues: Record<string, string | null> | null;
  proposedValues: Record<string, string | null>;
  note: string | null;
  status: AccountChangeStatus;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export function useAccountChangeRequests() {
  return useQuery<AccountChangeRequest[]>({
    queryKey: ['account-change-requests'],
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('account_change_requests')
        .select(
          'id,location_id,kind,current_values,proposed_values,note,status,review_note,reviewed_at,created_at',
        )
        .order('created_at', { ascending: false })
        .limit(25);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id as string,
        locationId: (row.location_id as string | null) ?? null,
        kind: row.kind as AccountChangeKind,
        currentValues: (row.current_values as Record<string, string | null> | null) ?? null,
        proposedValues: (row.proposed_values as Record<string, string | null>) ?? {},
        note: (row.note as string | null) ?? null,
        status: row.status as AccountChangeStatus,
        reviewNote: (row.review_note as string | null) ?? null,
        reviewedAt: (row.reviewed_at as string | null) ?? null,
        createdAt: row.created_at as string,
      }));
    },
  });
}
